import React, { useState, useEffect, useCallback } from 'react';
import { 
  Users, Search, Trash2, Eye, Download, 
  Filter, X, ChevronDown, ChevronUp, ChevronLeft, ChevronRight,
  MapPin, User, Check, Clock, XCircle, Hash, KeyRound, Edit, Save, ArrowRight, UserPlus, ArrowUp, ArrowDown, Wrench
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import SearchableSelect from '../../components/ui/SearchableSelect';
import DISTRICTS_BY_STATE from '../../data/indiaDistricts';
import PaginationBar from '../../components/PaginationBar';
import * as XLSX from 'xlsx';
import CommentThread from '../../components/CommentThread';
import { MemberSections, FamilyFormSection, FamilyTopView } from '../../components/FamilySections';
import { formatDisplayDate } from '../../lib/dateFormat';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const GENDER_OPTIONS = ['Male', 'Female'];
const GENDER_LABELS = { Male: 'Male / पुरुष', Female: 'Female / महिला' };

// Ensure the currently-stored value is always selectable (prevents selects rendering blank
// when a saved value isn't in the predefined options list).
const withCurrentOption = (options, value) => {
  const list = options || [];
  if (value && !list.includes(value)) return [value, ...list];
  return list;
};
const AGE_RANGES = [
  { label: 'All Ages', min: '', max: '' },
  { label: '0-18', min: 0, max: 18 },
  { label: '18-25', min: 18, max: 25 },
  { label: '25-35', min: 25, max: 35 },
  { label: '35-50', min: 35, max: 50 },
  { label: '50-65', min: 50, max: 65 },
  { label: '65+', min: 65, max: 100 },
];

const FamilyManagement = () => {
  const { token } = useAuth();
  const [registrations, setRegistrations] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 20;
  const [stats, setStats] = useState({
    totalFamilies: 0,
    totalPersons: 0,
    totalMale: 0,
    totalFemale: 0
  });
  
  // Status filter (Pending, Approved, Rejected)
  const [statusFilter, setStatusFilter] = useState('');
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [districtFilter, setDistrictFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [nukhFilter, setNukhFilter] = useState('');
  const [genderFilter, setGenderFilter] = useState('');
  const [ageRangeFilter, setAgeRangeFilter] = useState('');
  const [educationFilter, setEducationFilter] = useState('');
  const [occupationFilter, setOccupationFilter] = useState('');
  const [maritalStatusFilter, setMaritalStatusFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Status counts
  const [statusCounts, setStatusCounts] = useState({
    pending: 0,
    approved: 0,
    rejected: 0
  });
  
  // Options for filters
  const [filterOptions, setFilterOptions] = useState({
    states: [],
    nukhs: [],
    education: [],
    occupation: [],
    maritalStatus: []
  });
  
  // Detail modal
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // Delete confirmation
  const [deleteId, setDeleteId] = useState(null);
  const [deleteMember, setDeleteMember] = useState(null); // { regId, memberIndex, memberName }
  
  // Reset Password modal
  const [resetPasswordModal, setResetPasswordModal] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  
  // Edit states
  const [editingFamily, setEditingFamily] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [familyHistory, setFamilyHistory] = useState([]);
  const [showFamilyHistory, setShowFamilyHistory] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [editOptions, setEditOptions] = useState({
    countries: [], statesByCountry: {}, citiesByState: {}, citiesByCountry: {},
    nukhs: [], education: [], occupation: [], maritalStatus: [], relations: []
  });
  const [saving, setSaving] = useState(false);

  // Fetch dropdown options
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const response = await fetch(`${API_URL}/api/family-registration/options`);
        if (response.ok) {
          const data = await response.json();
          setFilterOptions({
            states: data.states || [],
            nukhs: data.nukhs || [],
            education: data.education || [],
            occupation: data.occupation || [],
            maritalStatus: data.maritalStatus || []
          });
          setEditOptions({
            countries: data.countries || [],
            statesByCountry: data.statesByCountry || {},
            citiesByState: data.citiesByState || {},
            citiesByCountry: data.citiesByCountry || {},
            nukhs: data.nukhs || [],
            education: data.education || [],
            occupation: data.occupation || [],
            maritalStatus: data.maritalStatus || [],
            relations: data.relations || [],
          });
        }
      } catch (error) {
        console.error('Error fetching options:', error);
      }
    };
    fetchOptions();
  }, []);

  // Fetch registrations
  const fetchRegistrations = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('skip', (currentPage - 1) * PAGE_SIZE);
      params.append('limit', PAGE_SIZE);
      if (searchQuery) params.append('search', searchQuery);
      if (stateFilter) params.append('state', stateFilter);
      if (districtFilter) params.append('district', districtFilter);
      if (cityFilter) params.append('city', cityFilter);
      if (nukhFilter) params.append('nukh', nukhFilter);
      if (statusFilter) params.append('status', statusFilter);
      if (genderFilter) params.append('gender', genderFilter);
      if (educationFilter) params.append('education', educationFilter);
      if (occupationFilter) params.append('occupation', occupationFilter);
      if (maritalStatusFilter) params.append('marital_status', maritalStatusFilter);
      if (ageRangeFilter) {
        const range = AGE_RANGES.find(r => r.label === ageRangeFilter);
        if (range && range.min !== '') {
          params.append('age_min', range.min);
          params.append('age_max', range.max);
        }
      }
      
      const response = await fetch(
        `${API_URL}/api/family-registrations?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setRegistrations(data.registrations || []);
        setTotalCount(data.total || 0);
      } else {
        toast.error('Failed to fetch registrations');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to fetch registrations');
    } finally {
      setLoading(false);
    }
  }, [token, currentPage, searchQuery, stateFilter, districtFilter, cityFilter, nukhFilter, statusFilter, genderFilter, ageRangeFilter, educationFilter, occupationFilter, maritalStatusFilter]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/family-registrations/stats/summary`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
        if (data.statusCounts) {
          setStatusCounts(data.statusCounts);
        }
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchRegistrations();
    fetchStats();
  }, [fetchRegistrations, fetchStats]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, stateFilter, districtFilter, cityFilter, nukhFilter, statusFilter, genderFilter, ageRangeFilter, educationFilter, occupationFilter, maritalStatusFilter]);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchRegistrations();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, stateFilter, nukhFilter, statusFilter, fetchRegistrations]);

  // Handle status update (Approve/Reject)
  const handleUpdateStatus = async (regId, newStatus, reason = '', comments = '') => {
    try {
      const response = await fetch(
        `${API_URL}/api/family-registrations/${regId}/status`,
        {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: newStatus, rejectionReason: reason, adminComments: comments })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Family ${newStatus}${data.familyId ? ` - ID: ${data.familyId}` : ''}`);
        fetchRegistrations();
        fetchStats();
        if (selectedFamily?.id === regId) {
          setSelectedFamily(prev => ({
            ...prev, status: newStatus,
            rejectionReason: newStatus === 'rejected' ? reason : '',
            adminComments: comments || prev?.adminComments || '',
          }));
        }
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to update status');
    }
  };

  // Rejection modal handlers
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [familyAdminComments, setFamilyAdminComments] = useState('');
  const [savingFamilyComments, setSavingFamilyComments] = useState(false);
  const [familyInternalComments, setFamilyInternalComments] = useState('');
  const [savingFamilyInternal, setSavingFamilyInternal] = useState(false);

  const handleRejectFamily = (regId) => {
    setRejectModal(regId);
    setRejectionReason('');
  };

  const confirmRejectFamily = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    await handleUpdateStatus(rejectModal, 'rejected', rejectionReason.trim());
    setRejectModal(null);
    setRejectionReason('');
  };

  const saveFamilyAdminComments = async (text) => {
    const regId = selectedFamily?.id;
    if (!regId) return;
    const response = await fetch(`${API_URL}/api/family-registrations/${regId}/comments`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ comment: text })
    });
    if (!response.ok) throw new Error('Failed to add comment');
    const data = await response.json();
    setSelectedFamily(prev => ({ ...prev, adminCommentsHistory: data.history || [], adminComments: data.adminComments || '' }));
    toast.success('Comment added. Family will be notified by email.');
  };

  const saveFamilyInternalComments = async (text) => {
    const regId = selectedFamily?.id;
    if (!regId) return;
    const response = await fetch(`${API_URL}/api/family-registrations/${regId}/internal-comments`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ comment: text })
    });
    if (!response.ok) throw new Error('Failed to add internal note');
    const data = await response.json();
    setSelectedFamily(prev => ({ ...prev, internalCommentsHistory: data.history || [], internalComments: data.internalComments || '' }));
    toast.success('Internal note added. Not shared with the family.');
  };

  // Delete registration
  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/family-registrations/${deleteId}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        toast.success('Registration deleted successfully');
        fetchRegistrations();
        fetchStats();
      } else {
        toast.error('Failed to delete registration');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to delete registration');
    } finally {
      setDeleteId(null);
    }
  };

  // Delete individual member
  const handleDeleteMember = async () => {
    if (!deleteMember) return;
    const { regId, memberIndex } = deleteMember;
    try {
      const response = await fetch(
        `${API_URL}/api/family-registrations/${regId}/members/${memberIndex}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        toast.success('Member removed successfully');
        fetchRegistrations();
        fetchStats();
        // Refresh selected family if viewing detail
        if (selectedFamily && selectedFamily.id === regId) {
          const updated = await fetch(`${API_URL}/api/family-registrations/${regId}`, { headers: { Authorization: `Bearer ${token}` } });
          if (updated.ok) {
            const data = await updated.json();
            setSelectedFamily(data.registration || data);
          }
        }
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to delete member');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to delete member');
    } finally {
      setDeleteMember(null);
    }
  };

  // One-time maintenance: assign member codes to any member missing one (all approved families)
  const [fixingIds, setFixingIds] = useState(false);
  const fixMemberIds = async () => {
    if (!window.confirm('Assign a unique Member ID to any family member that is missing one, across all approved families?\nThis is safe to run and only fills in missing IDs.')) return;
    setFixingIds(true);
    try {
      const response = await fetch(`${API_URL}/api/family-registrations/backfill-member-ids`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!response.ok) { toast.error('Failed to fix member IDs'); return; }
      const data = await response.json();
      if (data.membersFixed > 0) {
        toast.success(`Fixed ${data.membersFixed} member(s) across ${data.familiesUpdated} family(ies)`);
      } else {
        toast.success('All members already have IDs — nothing to fix');
      }
      fetchRegistrations();
    } catch (e) {
      toast.error('Failed to fix member IDs');
    } finally {
      setFixingIds(false);
    }
  };


  // Export to CSV
  const exportToCSV = async () => {
    try {
      const token = localStorage.getItem('token');
      // Fetch ALL records by paginating through in batches
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (searchQuery) params.append('search', searchQuery);
      if (stateFilter) params.append('state', stateFilter);
      if (nukhFilter) params.append('nukh', nukhFilter);

      let allRegistrations = [];
      let skip = 0;
      const batchSize = 200;
      let hasMore = true;

      while (hasMore) {
        const batchParams = new URLSearchParams(params);
        batchParams.append('skip', skip.toString());
        batchParams.append('limit', batchSize.toString());

        const response = await fetch(
          `${API_URL}/api/family-registrations?${batchParams.toString()}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (!response.ok) { toast.error('Failed to fetch data for export'); return; }
        const data = await response.json();
        const batch = data.registrations || [];
        allRegistrations = allRegistrations.concat(batch);
        
        if (batch.length < batchSize) {
          hasMore = false;
        } else {
          skip += batchSize;
        }
      }

      if (allRegistrations.length === 0) {
        toast.error('No data to export');
        return;
      }
    
    // Flatten family members for CSV - include ALL fields
    const rows = [];
    allRegistrations.forEach(reg => {
      if (!reg.familyMembers || reg.familyMembers.length === 0) {
        // Family with no members - still export family-level data
        rows.push({
          'Family ID': reg.familyId || reg.id || '',
          'Status': reg.status || '',
          'Family Nukh': reg.familyNukh || '',
          'Head Email': reg.headEmail || '',
          'Total Members': reg.totalMembers || '',
          'Total Male': reg.totalMale || '',
          'Total Female': reg.totalFemale || '',
          'Address': reg.currentPlace || '',
          'City / Village': reg.currentCity || '',
          'District': reg.currentDistrict || reg.district || '',
          'State': reg.currentState || reg.state || '',
          'Country': reg.currentCountry || reg.country || '',
          'Origin Address': reg.originPlace || '',
          'Origin City / Village': reg.originCity || '',
          'Origin District': reg.originDistrict || '',
          'Origin State': reg.originState || '',
          'Origin Country': reg.originCountry || '',
          'Member #': '',
          'Member ID': '',
          'Name': '',
          'Gender': '',
          'Relation with Head': '',
          'Father Name': '',
          "Father's Nukh": '',
          "Mother's Name": '',
          "Mother's Nukh": '',
          'Marital Status': '',
          'DOB': '',
          'Age': '',
          'Education': '',
          'Occupation': '',
          'Blood Group': '',
          'Spouse Name': '',
          'Mobile': '',
          'Email': '',
          'Social Media': '',
          'Maternal Name': '',
          'Maternal Nukh': '',
          'Maternal Place': '',
          'Other Info': '',
          'Registration Date': formatDisplayDate(reg.registrationDate),
          'Last Updated': formatDisplayDate(reg.lastUpdated)
        });
      } else {
        reg.familyMembers.forEach((member, idx) => {
          rows.push({
            'Family ID': reg.familyId || reg.id || '',
            'Status': reg.status || '',
            'Family Nukh': reg.familyNukh || '',
            'Head Email': reg.headEmail || '',
            'Total Members': reg.totalMembers || '',
            'Total Male': reg.totalMale || '',
            'Total Female': reg.totalFemale || '',
            'Address': reg.currentPlace || '',
            'City / Village': reg.currentCity || '',
            'District': reg.currentDistrict || reg.district || '',
            'State': reg.currentState || reg.state || '',
            'Country': reg.currentCountry || reg.country || '',
            'Origin Address': reg.originPlace || '',
            'Origin City / Village': reg.originCity || '',
            'Origin District': reg.originDistrict || '',
            'Origin State': reg.originState || '',
            'Origin Country': reg.originCountry || '',
            'Member #': idx + 1,
            'Member ID': member.memberId || '',
            'Name': member.name || '',
            'Gender': member.gender || '',
            'Relation with Head': member.relationWithHead || '',
            'Father Name': member.fatherName || '',
            "Father's Nukh": member.nukh || '',
            "Mother's Name": member.motherName || '',
            "Mother's Nukh": member.motherNukh || '',
            'Marital Status': member.maritalStatus || '',
            'DOB': formatDisplayDate(member.dob),
            'Age': member.age || '',
            'Education': member.education || '',
            'Occupation': member.occupation || '',
            'Blood Group': member.bloodGroup || '',
            'Spouse Name': member.spouseName || '',
            'Mobile': member.mobileNo || '',
            'Email': member.emailId || '',
            'Social Media': member.socialMediaId || '',
            'Maternal Name': member.maternalName || '',
            'Maternal Nukh': member.maternalNukh || '',
            'Maternal Place': member.maternalPlace || '',
            'Other Info': member.other || '',
            'Registration Date': reg.registrationDate ? new Date(reg.registrationDate).toLocaleDateString() : '',
            'Last Updated': reg.lastUpdated ? new Date(reg.lastUpdated).toLocaleDateString() : ''
          });
        });
      }
    });
    
    if (rows.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Family Registrations');
    XLSX.writeFile(wb, `family_registrations_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success(`Exported ${allRegistrations.length} families (${rows.length} rows)`);
    } catch (err) {
      toast.error('Export failed');
    }
  };

  // View family details
  const viewDetails = (family) => {
    setSelectedFamily(family);
    setShowDetailModal(true);
    setShowFamilyHistory(false);
    setEditingFamily(null);
    setFamilyHistory([]);
    setFamilyAdminComments(family.adminComments || '');
    setFamilyInternalComments(family.internalComments || '');
  };

  const fetchFamilyHistory = async (regId) => {
    setHistoryLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/family-registrations/${regId}/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setFamilyHistory(data.history || []);
      setShowFamilyHistory(true);
    } catch { toast.error('Failed to load edit history'); }
    finally { setHistoryLoading(false); }
  };

  // Clear filters
  const clearFilters = () => {
    setSearchQuery('');
    setStateFilter('');
    setDistrictFilter('');
    setCityFilter('');
    setNukhFilter('');
    setGenderFilter('');
    setAgeRangeFilter('');
    setEducationFilter('');
    setOccupationFilter('');
    setMaritalStatusFilter('');
    setStatusFilter('');
  };

  // Reset Password for family user
  const resetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setResettingPassword(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          email: resetPasswordModal.email,
          userType: 'family',
          newPassword: newPassword
        })
      });
      
      if (response.ok) {
        toast.success(`Password reset successfully for ${resetPasswordModal.headName}`);
        setResetPasswordModal(null);
        setNewPassword('');
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to reset password');
      }
    } catch (error) {
      toast.error('Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  // Open edit modal for family
  const openEditModal = (family) => {
    setEditingFamily(family);
    setEditFormData({
      headEmail: family.headEmail || family.email || '',
      currentCountry: family.currentCountry || family.country || '',
      currentState: family.currentState || family.state || '',
      currentDistrict: family.currentDistrict || family.district || '',
      currentCity: family.currentCity || '',
      currentPlace: family.currentPlace || '',
      originCountry: family.originCountry || '',
      originState: family.originState || '',
      originDistrict: family.originDistrict || '',
      originCity: family.originCity || '',
      originPlace: family.originPlace || '',
      familyNukh: family.familyNukh || '',
      status: family.status || 'pending',
      familyMembers: family.familyMembers || []
    });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleMemberEditChange = (memberIndex, field, value) => {
    setEditFormData(prev => {
      const updatedMembers = [...prev.familyMembers];
      updatedMembers[memberIndex] = { ...updatedMembers[memberIndex], [field]: value };
      
      // Auto-calculate age if DOB is changed
      if (field === 'dob' && value) {
        const birthDate = new Date(value);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        updatedMembers[memberIndex].age = age > 0 ? age : '';
      }
      
      return { ...prev, familyMembers: updatedMembers };
    });
  };

  // Reorder members within the array. Head (index 0) is locked in place.
  // direction: -1 = up, +1 = down.
  const moveMember = (memberIndex, direction) => {
    setEditFormData(prev => {
      const members = [...(prev.familyMembers || [])];
      const target = memberIndex + direction;
      // Head is always at index 0 and cannot be swapped.
      if (memberIndex <= 0 || target <= 0 || target >= members.length) return prev;
      [members[memberIndex], members[target]] = [members[target], members[memberIndex]];
      return { ...prev, familyMembers: members };
    });
  };

  const saveFamilyEdit = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/family-registrations/${editingFamily.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(editFormData)
      });
      
      if (response.ok) {
        toast.success('Family registration updated successfully');
        setEditingFamily(null);
        setSelectedFamily(prev => prev ? {
          ...prev,
          ...editFormData,
          totalMembers: editFormData.familyMembers?.length ?? prev.totalMembers,
        } : prev);
        fetchRegistrations();
        fetchStats();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to update family');
      }
    } catch (error) {
      toast.error('An error occurred while updating');
    } finally {
      setSaving(false);
    }
  };

  // Status badge component
  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Family Registrations</h1>
        <p className="text-gray-500">Manage family registration submissions</p>
      </div>

      {/* Status Filter Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        <Button
          variant={statusFilter === '' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStatusFilter('')}
          className="flex items-center gap-2"
        >
          <Users className="w-4 h-4" />
          All ({statusCounts.pending + statusCounts.approved + statusCounts.rejected})
        </Button>
        <Button
          variant={statusFilter === 'pending' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStatusFilter('pending')}
          className={`flex items-center gap-2 ${statusFilter !== 'pending' ? 'border-yellow-300 text-yellow-700 hover:bg-yellow-50' : 'bg-yellow-500 hover:bg-yellow-600'}`}
        >
          <Clock className="w-4 h-4" />
          Pending ({statusCounts.pending})
        </Button>
        <Button
          variant={statusFilter === 'approved' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStatusFilter('approved')}
          className={`flex items-center gap-2 ${statusFilter !== 'approved' ? 'border-green-300 text-green-700 hover:bg-green-50' : 'bg-green-500 hover:bg-green-600'}`}
        >
          <Check className="w-4 h-4" />
          Approved ({statusCounts.approved})
        </Button>
        <Button
          variant={statusFilter === 'rejected' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStatusFilter('rejected')}
          className={`flex items-center gap-2 ${statusFilter !== 'rejected' ? 'border-red-300 text-red-700 hover:bg-red-50' : 'bg-red-500 hover:bg-red-600'}`}
        >
          <XCircle className="w-4 h-4" />
          Rejected ({statusCounts.rejected})
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.totalFamilies}</p>
              <p className="text-sm text-gray-500">Families</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <User className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.totalPersons}</p>
              <p className="text-sm text-gray-500">Total Persons</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <User className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.totalMale}</p>
              <p className="text-sm text-gray-500">Male</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
              <User className="w-5 h-5 text-pink-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.totalFemale}</p>
              <p className="text-sm text-gray-500">Female</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, place, mobile, email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          
          {/* Filter Toggle */}
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2"
          >
            <Filter className="w-4 h-4" />
            Filters
            {(stateFilter || nukhFilter || districtFilter || cityFilter || genderFilter || ageRangeFilter || educationFilter || occupationFilter || maritalStatusFilter) && (
              <span className="bg-primary text-white text-xs px-1.5 py-0.5 rounded-full">
                {[stateFilter, districtFilter, cityFilter, nukhFilter, genderFilter, ageRangeFilter, educationFilter, occupationFilter, maritalStatusFilter].filter(Boolean).length}
              </span>
            )}
          </Button>
          
          {/* Export */}
          <Button
            onClick={exportToCSV}
            className="bg-green-600 hover:bg-green-700"
          >
            <Download className="w-4 h-4 mr-2" />
            Export Excel
          </Button>

          {/* Maintenance: fix any members missing a unique code */}
          <Button
            onClick={fixMemberIds}
            disabled={fixingIds}
            variant="outline"
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
            data-testid="fix-member-ids-btn"
            title="Assign Member IDs to any members missing one (approved families)"
          >
            <Wrench className="w-4 h-4 mr-2" />
            {fixingIds ? 'Fixing...' : 'Fix Member IDs'}
          </Button>
        </div>
        
        {/* Filter Options */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Location Filters</p>
            <div className="flex flex-wrap gap-4 mb-4">
              <div className="w-full md:w-auto">
                <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                <select
                  value={stateFilter}
                  onChange={(e) => { setStateFilter(e.target.value); setDistrictFilter(''); }}
                  className="w-full md:w-44 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  data-testid="admin-filter-state"
                >
                  <option value="">All States</option>
                  {filterOptions.states.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
              
              <div className="w-full md:w-auto">
                <label className="block text-sm font-medium text-gray-700 mb-1">District</label>
                <select
                  value={districtFilter}
                  onChange={(e) => setDistrictFilter(e.target.value)}
                  className="w-full md:w-44 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  disabled={!stateFilter}
                  data-testid="admin-filter-district"
                >
                  <option value="">All Districts</option>
                  {(DISTRICTS_BY_STATE[stateFilter] || []).map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>

              <div className="w-full md:w-auto">
                <label className="block text-sm font-medium text-gray-700 mb-1">City / Village</label>
                <input
                  type="text"
                  placeholder="Enter city/village"
                  value={cityFilter}
                  onChange={(e) => setCityFilter(e.target.value)}
                  className="w-full md:w-40 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  data-testid="admin-filter-city"
                />
              </div>

              <div className="w-full md:w-auto">
                <label className="block text-sm font-medium text-gray-700 mb-1">Nukh</label>
                <select
                  value={nukhFilter}
                  onChange={(e) => setNukhFilter(e.target.value)}
                  className="w-full md:w-44 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  data-testid="admin-filter-nukh"
                >
                  <option value="">All Nukhs</option>
                  {filterOptions.nukhs.map(nukh => (
                    <option key={nukh} value={nukh}>{nukh}</option>
                  ))}
                </select>
              </div>
            </div>

            <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Member Filters</p>
            <div className="flex flex-wrap gap-4">
              <div className="w-full md:w-auto">
                <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                <select
                  value={genderFilter}
                  onChange={(e) => setGenderFilter(e.target.value)}
                  className="w-full md:w-44 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  data-testid="admin-filter-gender"
                >
                  <option value="">All</option>
                  {GENDER_OPTIONS.map(g => (
                    <option key={g} value={g}>{GENDER_LABELS[g] || g}</option>
                  ))}
                </select>
              </div>

              <div className="w-full md:w-auto">
                <label className="block text-sm font-medium text-gray-700 mb-1">Age Range</label>
                <select
                  value={ageRangeFilter}
                  onChange={(e) => setAgeRangeFilter(e.target.value)}
                  className="w-full md:w-36 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  data-testid="admin-filter-age"
                >
                  {AGE_RANGES.map(r => (
                    <option key={r.label} value={r.label === 'All Ages' ? '' : r.label}>{r.label}</option>
                  ))}
                </select>
              </div>

              <div className="w-full md:w-auto">
                <label className="block text-sm font-medium text-gray-700 mb-1">Education</label>
                <select
                  value={educationFilter}
                  onChange={(e) => setEducationFilter(e.target.value)}
                  className="w-full md:w-44 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  data-testid="admin-filter-education"
                >
                  <option value="">All</option>
                  {(filterOptions.education || []).map(edu => (
                    <option key={edu} value={edu}>{edu}</option>
                  ))}
                </select>
              </div>

              <div className="w-full md:w-auto">
                <label className="block text-sm font-medium text-gray-700 mb-1">Occupation</label>
                <select
                  value={occupationFilter}
                  onChange={(e) => setOccupationFilter(e.target.value)}
                  className="w-full md:w-44 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  data-testid="admin-filter-occupation"
                >
                  <option value="">All</option>
                  {(filterOptions.occupation || []).map(occ => (
                    <option key={occ} value={occ}>{occ}</option>
                  ))}
                </select>
              </div>

              <div className="w-full md:w-auto">
                <label className="block text-sm font-medium text-gray-700 mb-1">Marital Status</label>
                <select
                  value={maritalStatusFilter}
                  onChange={(e) => setMaritalStatusFilter(e.target.value)}
                  className="w-full md:w-44 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  data-testid="admin-filter-marital-status"
                >
                  <option value="">All</option>
                  {(filterOptions.maritalStatus || []).map(ms => (
                    <option key={ms} value={ms}>{ms}</option>
                  ))}
                </select>
              </div>
              
              {(stateFilter || nukhFilter || districtFilter || cityFilter || genderFilter || ageRangeFilter || educationFilter || occupationFilter || maritalStatusFilter) && (
                <div className="flex items-end">
                  <Button variant="ghost" onClick={clearFilters} size="sm">
                    <X className="w-4 h-4 mr-1" /> Clear Filters
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Registrations Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-500">Loading registrations...</p>
          </div>
        ) : registrations.length === 0 ? (
          <div className="p-12 text-center">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No family registrations found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Family ID</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Family Head</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Email</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Location</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Nukh</th>
                  <th className="text-center px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Members</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {registrations.map((reg) => {
                  const head = reg.familyMembers?.find(m => m.relationWithHead === 'Head - Self') || reg.familyMembers?.[0];
                  return (
                    <tr key={reg.id} className="hover:bg-gray-50" data-testid={`family-row-${reg.id}`}>
                      <td className="px-6 py-4">
                        {reg.familyId ? (
                          <div className="flex items-center gap-2">
                            <Hash className="w-4 h-4 text-primary" />
                            <span className="font-mono font-bold text-primary">{reg.familyId}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm italic">Not assigned</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{head?.name || 'N/A'}</p>
                            <p className="text-sm text-gray-500">{head?.occupation || 'N/A'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {reg.email || reg.headEmail ? (
                          <a 
                            href={`mailto:${reg.email || reg.headEmail}`} 
                            className="text-sm text-blue-600 hover:underline"
                          >
                            {reg.email || reg.headEmail}
                          </a>
                        ) : (
                          <span className="text-gray-400 text-sm italic">N/A</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-start gap-2">
                          <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                          <div>
                            <p className="text-sm text-gray-900">{reg.currentCity || reg.currentPlace}</p>
                            <p className="text-xs text-gray-500">{reg.currentDistrict || reg.district}, {reg.currentState || reg.state}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded">
                          {reg.familyNukh}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                            {reg.totalMembers} Total
                          </span>
                          <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded">
                            {reg.totalMale}M / {reg.totalFemale}F
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(reg.status || 'pending')}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {formatDisplayDate(reg.registrationDate)}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => viewDetails(reg)}
                            className="text-blue-600 hover:text-blue-800"
                            data-testid={`view-btn-${reg.id}`}
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {(reg.headEmail || reg.email) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const head = reg.familyMembers?.find(m => m.relationWithHead === 'Head - Self') || reg.familyMembers?.[0];
                                setResetPasswordModal({
                                  email: reg.headEmail || reg.email,
                                  headName: head?.name || 'Family Head'
                                });
                              }}
                              className="text-blue-600 hover:text-blue-800"
                              title="Reset Password"
                            >
                              <KeyRound className="w-4 h-4" />
                            </Button>
                          )}
                          {(!reg.status || reg.status === 'pending') && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleUpdateStatus(reg.id, 'approved')}
                                className="text-green-600 hover:text-green-800 hover:bg-green-50"
                                title="Approve"
                                data-testid={`approve-btn-${reg.id}`}
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRejectFamily(reg.id)}
                                className="text-red-600 hover:text-red-800 hover:bg-red-50"
                                title="Reject"
                                data-testid={`reject-btn-${reg.id}`}
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { viewDetails(reg); openEditModal(reg); }}
                            className="text-orange-600 hover:text-orange-800"
                            title="Edit Family"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteId(reg.id)}
                            className="text-red-600 hover:text-red-800"
                            data-testid={`delete-btn-${reg.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination */}
            {totalCount > PAGE_SIZE && (
              <div className="px-4 py-3 border-t border-gray-200 bg-gray-50/50 rounded-b-xl" data-testid="families-pagination">
                <PaginationBar
                  currentPage={currentPage}
                  totalPages={Math.ceil(totalCount / PAGE_SIZE)}
                  onPageChange={(p) => setCurrentPage(p)}
                  totalItems={totalCount}
                  pageSize={PAGE_SIZE}
                  itemLabel="families"
                  testIdPrefix="admin-families"
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedFamily && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Family Details</h2>
                <div className="flex items-center gap-3 mt-1">
                  {selectedFamily.familyId ? (
                    <span className="text-sm font-mono font-bold text-primary bg-primary/10 px-2 py-0.5 rounded">
                      Family ID: {selectedFamily.familyId}
                    </span>
                  ) : (
                    <span className="text-sm text-gray-500">ID not yet assigned</span>
                  )}
                  {getStatusBadge(selectedFamily.status || 'pending')}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!editingFamily && (
                  <Button variant="outline" size="sm" onClick={() => openEditModal(selectedFamily)} className="text-orange-700 border-orange-300 hover:bg-orange-50" data-testid="family-edit-btn">
                    <Edit className="w-4 h-4 mr-1" /> Edit
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={() => fetchFamilyHistory(selectedFamily.id)} disabled={historyLoading} data-testid="family-history-btn">
                  {historyLoading ? <Clock className="w-4 h-4 animate-spin mr-1" /> : <Clock className="w-4 h-4 mr-1" />}
                  History
                </Button>
                <Button variant="ghost" size="sm" onClick={() => { setEditingFamily(null); setShowDetailModal(false); }}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              {editingFamily ? (
              <div className="space-y-6" data-testid="family-inline-edit-form">
                {/* Current Location */}
                <div>
                  <h3 className="font-semibold text-gray-700 mb-3">Current Place / वर्तमान निवास</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Country / देश</Label>
                      <select name="currentCountry" value={editFormData.currentCountry || ''} onChange={(e) => { setEditFormData(prev => ({ ...prev, currentCountry: e.target.value, currentState: '', currentDistrict: '', currentCity: '' })); }} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" data-testid="edit-current-country">
                        <option value="">Select Country</option>
                        {editOptions.countries.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label>State / राज्य</Label>
                      <select name="currentState" value={editFormData.currentState || ''} onChange={(e) => { setEditFormData(prev => ({ ...prev, currentState: e.target.value, currentDistrict: '', currentCity: '' })); }} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" data-testid="edit-current-state">
                        <option value="">Select State</option>
                        {(editOptions.statesByCountry[editFormData.currentCountry] || editOptions.statesByCountry['India'] || []).map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label>District / जिला</Label>
                      <SearchableSelect options={DISTRICTS_BY_STATE[editFormData.currentState] || []} value={editFormData.currentDistrict || ''} onChange={(val) => setEditFormData(prev => ({ ...prev, currentDistrict: val }))} placeholder="Select district..." allowCustom={true} disabled={!editFormData.currentState} />
                    </div>
                    <div>
                      <Label>City / Village / शहर / गाँव</Label>
                      <SearchableSelect options={editOptions.citiesByState[editFormData.currentState] || []} value={editFormData.currentCity || ''} onChange={(val) => setEditFormData(prev => ({ ...prev, currentCity: val }))} placeholder="Select or type city/village..." allowCustom={true} />
                    </div>
                    <div className="md:col-span-2">
                      <Label>Address / पता</Label>
                      <Input name="currentPlace" value={editFormData.currentPlace || ''} onChange={handleEditChange} placeholder="Enter full address" />
                    </div>
                  </div>
                </div>

                {/* Origin Location */}
                <div>
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <h3 className="font-semibold text-gray-700">Native Place / मूल निवास</h3>
                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none" data-testid="admin-edit-same-as-current">
                      <input
                        type="checkbox"
                        onChange={(e) => { if (e.target.checked) { setEditFormData(prev => ({ ...prev, originCountry: prev.currentCountry, originState: prev.currentState, originDistrict: prev.currentDistrict, originCity: prev.currentCity, originPlace: prev.currentPlace })); } }}
                        className="w-4 h-4 accent-primary rounded"
                      />
                      Same as Current Place <span className="text-gray-400 text-xs">(वर्तमान निवास के समान)</span>
                    </label>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Country / देश</Label>
                      <select name="originCountry" value={editFormData.originCountry || ''} onChange={(e) => { setEditFormData(prev => ({ ...prev, originCountry: e.target.value, originState: '', originDistrict: '', originCity: '' })); }} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" data-testid="edit-origin-country">
                        <option value="">Select Country</option>
                        {editOptions.countries.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label>State / राज्य</Label>
                      <select name="originState" value={editFormData.originState || ''} onChange={(e) => { setEditFormData(prev => ({ ...prev, originState: e.target.value, originDistrict: '', originCity: '' })); }} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" data-testid="edit-origin-state">
                        <option value="">Select State</option>
                        {(editOptions.statesByCountry[editFormData.originCountry] || editOptions.statesByCountry['India'] || []).map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label>District / जिला</Label>
                      <SearchableSelect options={DISTRICTS_BY_STATE[editFormData.originState] || []} value={editFormData.originDistrict || ''} onChange={(val) => setEditFormData(prev => ({ ...prev, originDistrict: val }))} placeholder="Select district..." allowCustom={true} disabled={!editFormData.originState} />
                    </div>
                    <div>
                      <Label>City / Village / शहर / गाँव</Label>
                      <SearchableSelect options={editOptions.citiesByState[editFormData.originState] || []} value={editFormData.originCity || ''} onChange={(val) => setEditFormData(prev => ({ ...prev, originCity: val }))} placeholder="Select or type city/village..." allowCustom={true} />
                    </div>
                    <div className="md:col-span-2">
                      <Label>Address / पता</Label>
                      <Input name="originPlace" value={editFormData.originPlace || ''} onChange={handleEditChange} placeholder="Enter full address" />
                    </div>
                  </div>
                </div>

                {/* Family Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Family Nukh / परिवार नुख</Label>
                    <SearchableSelect options={editOptions.nukhs} value={editFormData.familyNukh || ''} onChange={(val) => setEditFormData(prev => ({ ...prev, familyNukh: val }))} placeholder="Search nukh..." allowCustom={true} />
                  </div>
                  <div>
                    <Label>Status / स्थिति</Label>
                    <select name="status" value={editFormData.status} onChange={handleEditChange} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <Label>Family Head Login Email / परिवार मुखिया लॉगिन ईमेल</Label>
                    <Input type="email" name="headEmail" value={editFormData.headEmail || ''} onChange={handleEditChange} placeholder="head@example.com" data-testid="edit-head-email" />
                    <p className="text-xs text-gray-500 mt-1">Changing this updates the login email the family head uses to sign in.</p>
                  </div>
                </div>

                {/* Family Members Section — sectioned layout */}
                <div>
                  <h3 className="font-semibold text-gray-700 mb-3">Family Members / परिवार के सदस्य ({editFormData.familyMembers?.length || 0})</h3>
                  <div className="space-y-4">
                    {editFormData.familyMembers?.map((member, idx) => (
                      <div key={member.id || idx} className={`border rounded-lg p-4 ${member.relationWithHead === 'Head - Self' ? 'border-primary bg-primary/5' : 'border-gray-200'}`}>
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-2">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${member.relationWithHead === 'Head - Self' ? 'bg-primary text-white' : 'bg-gray-200'}`}>{idx + 1}</span>
                            <span className="font-medium">{member.name || `Member ${idx + 1}`}</span>
                            {member.relationWithHead === 'Head - Self' && <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded">Head</span>}
                            {member.memberId && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded font-mono">{member.memberId}</span>}
                          </div>
                          <div className="flex items-center gap-1">
                            {member.relationWithHead !== 'Head - Self' && (
                              <>
                                <button type="button" onClick={() => moveMember(idx, -1)} disabled={idx <= 1} title="Move up" data-testid={`admin-member-move-up-${idx}`} className="text-gray-500 hover:text-primary hover:bg-primary/10 p-1.5 rounded-full transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                                  <ArrowUp className="w-4 h-4" />
                                </button>
                                <button type="button" onClick={() => moveMember(idx, 1)} disabled={idx >= (editFormData.familyMembers?.length || 0) - 1} title="Move down" data-testid={`admin-member-move-down-${idx}`} className="text-gray-500 hover:text-primary hover:bg-primary/10 p-1.5 rounded-full transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                                  <ArrowDown className="w-4 h-4" />
                                </button>
                                <button type="button" onClick={() => { setEditFormData(prev => ({ ...prev, familyMembers: prev.familyMembers.filter((_, i) => i !== idx) })); }} className="text-red-400 hover:text-red-600 hover:bg-red-50 p-1.5 rounded-full transition-colors" title="Remove member" data-testid={`admin-member-remove-${idx}`}>
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </div>

                        <FamilyFormSection sectionKey="basic" columns={3}>
                          <div>
                            <Label className="text-xs">Name / नाम</Label>
                            <Input value={member.name || ''} onChange={(e) => handleMemberEditChange(idx, 'name', e.target.value)} className="h-9 text-sm" />
                          </div>
                          <div>
                            <Label className="text-xs">Relation with Head / मुखिया से संबंध</Label>
                            <select value={member.relationWithHead || ''} onChange={(e) => handleMemberEditChange(idx, 'relationWithHead', e.target.value)} className="w-full h-9 px-2 rounded-md border border-input bg-background text-sm" disabled={member.relationWithHead === 'Head - Self'}>
                              <option value="">Select</option>
                              {withCurrentOption(editOptions.relations, member.relationWithHead).map(r => <option key={r} value={r}>{r}</option>)}
                            </select>
                          </div>
                          <div>
                            <Label className="text-xs">Gender / लिंग</Label>
                            <select value={member.gender || ''} onChange={(e) => handleMemberEditChange(idx, 'gender', e.target.value)} className="w-full h-9 px-2 rounded-md border border-input bg-background text-sm">
                              <option value="">Select</option>
                              {GENDER_OPTIONS.map(g => <option key={g} value={g}>{GENDER_LABELS[g] || g}</option>)}
                              {member.gender && !GENDER_OPTIONS.includes(member.gender) && <option value={member.gender}>{member.gender}</option>}
                            </select>
                          </div>
                        </FamilyFormSection>

                        <FamilyFormSection sectionKey="personal" columns={3}>
                          <div>
                            <Label className="text-xs">Marital Status / वैवाहिक स्थिति</Label>
                            <select value={member.maritalStatus || ''} onChange={(e) => handleMemberEditChange(idx, 'maritalStatus', e.target.value)} className="w-full h-9 px-2 rounded-md border border-input bg-background text-sm">
                              <option value="">Select</option>
                              {withCurrentOption(editOptions.maritalStatus, member.maritalStatus).map(ms => <option key={ms} value={ms}>{ms}</option>)}
                            </select>
                          </div>
                          <div>
                            <Label className="text-xs">DOB / जन्म तिथि</Label>
                            <Input type="date" value={member.dob || ''} onChange={(e) => handleMemberEditChange(idx, 'dob', e.target.value)} className="h-9 text-sm" />
                          </div>
                          <div>
                            <Label className="text-xs">Age / आयु</Label>
                            <Input type="number" value={member.age || ''} onChange={(e) => handleMemberEditChange(idx, 'age', e.target.value)} className="h-9 text-sm" />
                          </div>
                          <div>
                            <Label className="text-xs">Education / शिक्षा</Label>
                            <select value={member.education || ''} onChange={(e) => handleMemberEditChange(idx, 'education', e.target.value)} className="w-full h-9 px-2 rounded-md border border-input bg-background text-sm">
                              <option value="">Select</option>
                              {withCurrentOption(editOptions.education, member.education).map(e => <option key={e} value={e}>{e}</option>)}
                            </select>
                          </div>
                          <div>
                            <Label className="text-xs">Occupation / व्यवसाय</Label>
                            <select value={member.occupation || ''} onChange={(e) => handleMemberEditChange(idx, 'occupation', e.target.value)} className="w-full h-9 px-2 rounded-md border border-input bg-background text-sm">
                              <option value="">Select</option>
                              {withCurrentOption(editOptions.occupation, member.occupation).map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                          </div>
                          <div>
                            <Label className="text-xs">Blood Group / रक्त समूह</Label>
                            <select value={member.bloodGroup || ''} onChange={(e) => handleMemberEditChange(idx, 'bloodGroup', e.target.value)} className="w-full h-9 px-2 rounded-md border border-input bg-background text-sm">
                              <option value="">Select</option>
                              {['A+','A-','B+','B-','AB+','AB-','O+','O-'].map(bg => <option key={bg} value={bg}>{bg}</option>)}
                            </select>
                          </div>
                          <div>
                            <Label className="text-xs">Spouse Name / जीवनसाथी <span className="text-gray-400">(optional)</span></Label>
                            <Input value={member.spouseName || ''} onChange={(e) => handleMemberEditChange(idx, 'spouseName', e.target.value)} className="h-9 text-sm" data-testid={`admin-member-${idx}-spouse-name`} />
                          </div>
                        </FamilyFormSection>

                        <FamilyFormSection sectionKey="paternal" columns={2}>
                          <div>
                            <Label className="text-xs">Father's Name / पिता का नाम</Label>
                            <Input value={member.fatherName || ''} onChange={(e) => handleMemberEditChange(idx, 'fatherName', e.target.value)} className="h-9 text-sm" />
                          </div>
                          <div>
                            <Label className="text-xs">Father's Nukh / पिता का नुख</Label>
                            <SearchableSelect options={editOptions.nukhs} value={member.nukh || ''} onChange={(val) => handleMemberEditChange(idx, 'nukh', val)} placeholder="Select nukh..." allowCustom={true} />
                          </div>
                          <div>
                            <Label className="text-xs">Mother's Name / माता का नाम <span className="text-gray-400">(optional)</span></Label>
                            <Input value={member.motherName || ''} onChange={(e) => handleMemberEditChange(idx, 'motherName', e.target.value)} className="h-9 text-sm" />
                          </div>
                          <div>
                            <Label className="text-xs">Mother's Nukh / माता का नुख <span className="text-gray-400">(optional)</span></Label>
                            <SearchableSelect options={editOptions.nukhs} value={member.motherNukh || ''} onChange={(val) => handleMemberEditChange(idx, 'motherNukh', val)} placeholder="Select nukh..." allowCustom={true} />
                          </div>
                        </FamilyFormSection>

                        <FamilyFormSection sectionKey="maternal" columns={3}>
                          <div>
                            <Label className="text-xs">Maternal's Name / नाना-मामा का नाम</Label>
                            <Input value={member.maternalName || ''} onChange={(e) => handleMemberEditChange(idx, 'maternalName', e.target.value)} className="h-9 text-sm" />
                          </div>
                          <div>
                            <Label className="text-xs">Maternal's Nukh / मातृ नुख</Label>
                            <SearchableSelect options={editOptions.nukhs} value={member.maternalNukh || ''} onChange={(val) => handleMemberEditChange(idx, 'maternalNukh', val)} placeholder="Select nukh..." allowCustom={true} />
                          </div>
                          <div>
                            <Label className="text-xs">Maternal's Place / मातृ स्थान</Label>
                            <Input value={member.maternalPlace || ''} onChange={(e) => handleMemberEditChange(idx, 'maternalPlace', e.target.value)} className="h-9 text-sm" />
                          </div>
                        </FamilyFormSection>

                        <FamilyFormSection sectionKey="contact" columns={3}>
                          <div>
                            <Label className="text-xs">Email Address / ईमेल</Label>
                            <Input value={member.emailId || ''} onChange={(e) => handleMemberEditChange(idx, 'emailId', e.target.value)} className="h-9 text-sm" />
                          </div>
                          <div>
                            <Label className="text-xs">Mobile No / मोबाइल</Label>
                            <Input value={member.mobileNo || ''} onChange={(e) => handleMemberEditChange(idx, 'mobileNo', e.target.value)} className="h-9 text-sm" />
                          </div>
                          <div>
                            <Label className="text-xs">Social Media / सोशल मीडिया</Label>
                            <Input value={member.socialMediaId || ''} onChange={(e) => handleMemberEditChange(idx, 'socialMediaId', e.target.value)} className="h-9 text-sm" placeholder="@username" />
                          </div>
                        </FamilyFormSection>

                        <FamilyFormSection sectionKey="other" columns={1} className="mb-0">
                          <div>
                            <Label className="text-xs">Other Information / अन्य जानकारी</Label>
                            <textarea value={member.other || ''} onChange={(e) => handleMemberEditChange(idx, 'other', e.target.value)} className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm" rows={2} />
                          </div>
                        </FamilyFormSection>
                      </div>
                    ))}
                  </div>
                  <Button type="button" variant="outline" className="w-full mt-4 border-dashed border-2 text-gray-600 hover:text-primary hover:border-primary" onClick={() => { setEditFormData(prev => ({ ...prev, familyMembers: [...(prev.familyMembers || []), { id: Date.now().toString() + Math.random().toString(36).substr(2, 9), name: '', gender: '', fatherName: '', nukh: '', motherName: '', motherNukh: '', relationWithHead: '', dob: '', age: '', education: '', occupation: '', maritalStatus: '', mobileNo: '', emailId: '', socialMediaId: '', maternalName: '', maternalNukh: '', maternalPlace: '', spouseName: '', bloodGroup: '', other: '' }] })); }} data-testid="admin-family-add-member">
                    <UserPlus className="w-4 h-4 mr-2" /> Add New Member / नया सदस्य जोड़ें
                  </Button>
                </div>

                <div className="flex justify-end gap-3 mt-2 pt-4 border-t">
                  <Button variant="outline" onClick={() => setEditingFamily(null)} data-testid="family-edit-cancel">Cancel</Button>
                  <Button onClick={saveFamilyEdit} disabled={saving} className="bg-orange-600 hover:bg-orange-700" data-testid="family-edit-save">
                    {saving ? 'Saving...' : (<><Save className="w-4 h-4 mr-2" /> Save Changes</>)}
                  </Button>
                </div>
              </div>
              ) : (
              <>
              {/* Edit History Panel */}
              {showFamilyHistory && (
                <div className="mb-4 bg-purple-50/50 border border-purple-200 rounded-lg p-4" data-testid="family-history-panel">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-purple-600" />
                      <h4 className="font-semibold text-sm text-purple-900">Edit History / Audit Trail</h4>
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">{familyHistory.length} entries</span>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => setShowFamilyHistory(false)} className="text-purple-600 h-7 px-2">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  {familyHistory.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-3">No edit history recorded yet.</p>
                  ) : (
                    <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                      {familyHistory.map((entry, idx) => (
                        <div key={entry.id || idx} className="bg-white rounded-lg border border-gray-100 p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                entry.changeType === 'status_change' ? 'bg-blue-100 text-blue-700' :
                                entry.editedByRole === 'admin' ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'
                              }`}>
                                {entry.changeType === 'status_change' ? 'Status Change' : entry.editedByRole === 'admin' ? 'Admin Edit' : 'User Edit'}
                              </span>
                              <span className="text-xs text-gray-500">by <span className="font-medium">{entry.editedBy}</span></span>
                            </div>
                            <span className="text-xs text-gray-400">
                              {new Date(entry.timestamp).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}{' '}
                              {new Date(entry.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                          <div className="space-y-1">
                            {entry.changes?.map((change, cidx) => (
                              <div key={cidx} className="flex items-center gap-2 text-xs py-1 border-t border-gray-50 first:border-t-0">
                                <span className="font-medium text-gray-700 min-w-[120px]">{change.label}:</span>
                                <span className="text-red-500 line-through truncate max-w-[150px]" title={change.oldValue}>{change.oldValue}</span>
                                <ArrowRight className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                <span className="text-green-600 font-medium truncate max-w-[150px]" title={change.newValue}>{change.newValue}</span>
                              </div>
                            ))}
                          </div>
                          <p className="text-xs text-gray-400 mt-1">{entry.totalChanges} field(s) changed</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Rejection Reason Display */}
              {selectedFamily.rejectionReason && selectedFamily.status === 'rejected' && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4" data-testid="family-rejection-reason-card">
                  <div className="flex items-start gap-2">
                    <XCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="text-sm font-semibold text-red-800">Rejection Reason</h4>
                      <p className="text-sm text-red-700 mt-1 whitespace-pre-wrap">{selectedFamily.rejectionReason}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Admin Comments Section — threaded, chronological history */}
              <CommentThread
                entries={selectedFamily.adminCommentsHistory || []}
                legacyText={selectedFamily.adminComments || ''}
                onSubmit={saveFamilyAdminComments}
                title="Admin Comments / Feedback"
                subtitle="Each new entry is visible on the family's dashboard and emailed to them."
                testIdPrefix="family-admin-comments"
                variant="amber"
              />

              {/* Internal Comments — committee only, never shown/emailed to family */}
              <CommentThread
                entries={selectedFamily.internalCommentsHistory || []}
                legacyText={selectedFamily.internalComments || ''}
                onSubmit={saveFamilyInternalComments}
                title="Internal Comments"
                subtitle="These notes are never emailed or shown to the family."
                testIdPrefix="family-internal-comments"
                variant="gray"
              />

              {/* Family Location Info */}
              <div className="mb-6">
                <FamilyTopView family={selectedFamily} showHeadEmail />
              </div>
              
              {/* Family Members */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 uppercase mb-3">
                  Family Members ({selectedFamily.totalMembers})
                </h3>
                <div className="space-y-4">
                  {selectedFamily.familyMembers?.map((member, index) => (
                    <FamilyMemberCard
                      key={member.id || index}
                      member={member}
                      index={index}
                      totalMembers={selectedFamily.familyMembers?.length || 0}
                      onDelete={() => setDeleteMember({ regId: selectedFamily.id, memberIndex: index, memberName: member.name })}
                    />
                  ))}
                </div>
              </div>

              {/* Approve/Reject Actions in Modal */}
              {(!selectedFamily.status || selectedFamily.status === 'pending') && (
                <div className="mt-6 pt-4 border-t border-gray-200 flex gap-4">
                  <Button
                    onClick={() => {
                      handleUpdateStatus(selectedFamily.id, 'approved');
                      setShowDetailModal(false);
                    }}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    data-testid="modal-approve-btn"
                  >
                    <Check className="w-4 h-4 mr-2" /> Approve Family
                  </Button>
                  <Button
                    onClick={() => {
                      handleRejectFamily(selectedFamily.id);
                    }}
                    variant="destructive"
                    className="flex-1"
                    data-testid="modal-reject-btn"
                  >
                    <XCircle className="w-4 h-4 mr-2" /> Reject
                  </Button>
                </div>
              )}
              </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Registration?</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this family registration? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setDeleteId(null)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700"
                onClick={handleDelete}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Member Confirmation Modal */}
      {deleteMember && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Member?</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to remove <strong>{deleteMember.memberName}</strong> from this family? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setDeleteMember(null)}>
                Cancel
              </Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700" onClick={handleDeleteMember} data-testid="confirm-delete-member">
                Delete Member
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetPasswordModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setResetPasswordModal(null)}>
          <div className="bg-white rounded-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <KeyRound className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="font-bold text-xl text-gray-900">Reset Password</h2>
                <p className="text-sm text-gray-500">पासवर्ड रीसेट करें</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-600 mb-1">Resetting password for:</p>
              <p className="font-semibold text-gray-900">{resetPasswordModal.headName}</p>
              <p className="text-sm text-gray-500">{resetPasswordModal.email}</p>
            </div>

            <form onSubmit={resetPassword} className="space-y-4">
              <div>
                <Label htmlFor="newFamilyPassword">New Password / नया पासवर्ड</Label>
                <Input
                  id="newFamilyPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 6 characters)"
                  required
                  minLength={6}
                />
              </div>

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setResetPasswordModal(null);
                    setNewPassword('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  disabled={resettingPassword}
                >
                  {resettingPassword ? 'Resetting...' : 'Reset Password'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Rejection Reason Modal */}
      {rejectModal && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={() => setRejectModal(null)}>
          <div className="bg-white rounded-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()} data-testid="family-reject-modal">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h2 className="font-bold text-lg text-gray-900">Reject Registration</h2>
                <p className="text-sm text-gray-500">रजिस्ट्रेशन अस्वीकार करें</p>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium">Reason for Rejection <span className="text-red-500">*</span></label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Enter the reason for rejection (will be visible to the applicant)..."
                  rows={4}
                  className="w-full mt-1 text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-300"
                  data-testid="family-reject-reason-input"
                />
              </div>
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => setRejectModal(null)}>Cancel</Button>
                <Button
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  onClick={confirmRejectFamily}
                  disabled={!rejectionReason.trim()}
                  data-testid="family-reject-confirm-btn"
                >
                  <X className="w-4 h-4 mr-1" /> Confirm Rejection
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Family Member Card Component
const FamilyMemberCard = ({ member, index, totalMembers, onDelete }) => {
  const [expanded, setExpanded] = useState(index === 0);
  
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div
        className={`flex items-center justify-between p-4 cursor-pointer ${
          member.relationWithHead === 'Head - Self' ? 'bg-primary/10' : 'bg-gray-50'
        }`}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
            member.relationWithHead === 'Head - Self' ? 'bg-primary text-white' : 'bg-gray-300 text-gray-700'
          }`}>
            {index + 1}
          </div>
          <div>
            <p className="font-medium text-gray-900">
              {member.name}
              {member.relationWithHead === 'Head - Self' && (
                <span className="text-primary text-sm ml-2">(Head)</span>
              )}
            </p>
            <div className="flex items-center gap-2">
              <p className="text-sm text-gray-500">
                {member.relationWithHead} • {member.gender} • {member.age ? `${member.age} yrs` : 'Age N/A'}
              </p>
              {member.memberId && (
                <span className="text-xs font-mono font-bold text-green-700 bg-green-100 px-1.5 py-0.5 rounded">
                  ID: {member.memberId}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {onDelete && totalMembers > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="p-1.5 text-red-500 hover:bg-red-50 rounded-md transition-colors"
              title="Delete this member"
              data-testid={`delete-member-${index}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          {expanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </div>
      </div>
      
      {expanded && (
        <div className="p-4 border-t border-gray-200 bg-white">
          <MemberSections member={member} />
        </div>
      )}
    </div>
  );
};

export default FamilyManagement;
